/*
* === DocumentUploadStep.js (Final - Cloudinary Ready) ===
*/
import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography, Button, Box, Alert, List, ListItem, ListItemText,
  ListItemIcon, LinearProgress, Paper, CircularProgress
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useOutletContext } from 'react-router-dom';
import api from '../../services/api'; 
import { useAuth } from '../../context/AuthContext';

/* --------------------------------------------------------------
   Helper: tiny preview URL (Fixed for GCS Vault)
   -------------------------------------------------------------- */
const getPreviewUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
    }
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://brightsparc-api-520983012715.africa-south1.run.app';
    
    // 🚨 CRITICAL FIX: Route to the secure vault proxy
    const cleanPath = path.replace(/\\/g, '/').replace(/^\/?vault\//, '').replace(/^\/?uploads\//, '');
    return `${baseUrl}/vault/${cleanPath}`;
};

/* --------------------------------------------------------------
   DocumentManagerItem
   -------------------------------------------------------------- */
const DocumentManagerItem = ({
  documentType,
  label,
  applicationId,
  token,
  personalDocuments,
  applicationDocuments,
  refreshData,
}) => {
  const [status, setStatus] = useState('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');

  const attachedDoc = applicationDocuments.find(d => d.FileType === documentType || d.DocumentType === documentType);
  
  const repoDoc = personalDocuments.find(d => 
    (d.DocumentType || '').toUpperCase() === documentType.toUpperCase() || 
    (d.FileType || '').toUpperCase() === documentType.toUpperCase()
  );

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('document', file);
    formData.append('documentType', documentType);
    formData.append('documentLabel', label); 
    formData.append('applicationId', applicationId);

    setStatus('uploading');
    setError('');
    setProgress(0);

    try {
      await api.post('/applicant/documents', formData, { 
        headers: { Authorization: `Bearer ${token}` },
        onUploadProgress: p => setProgress(Math.round((p.loaded * 100) / p.total)),
      });
      await refreshData();
      setStatus('success');
      setTimeout(() => setStatus('idle'), 2000);
    } catch (err) {
      setStatus('idle');
      setError(err.response?.data?.message || 'Upload failed');
    }
  };

  const handleAttach = async () => {
    if (!repoDoc) return;
    setStatus('attaching');
    setError('');
    try {
      await api.post(
        '/applicant/attach-document',
        { userDocumentId: repoDoc.Id, applicationId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshData();
      setStatus('idle');
    } catch (err) {
      setStatus('idle');
      setError(err.response?.data?.message || 'Attach failed');
    }
  };

  return (
    <ListItem divider>
      <ListItemIcon>
        {attachedDoc ? <CheckCircleIcon color="success" /> : <UploadFileIcon color="action" />}
      </ListItemIcon>

      <ListItemText
        primary={label}
        secondary={
            attachedDoc ? (
                <span>
                    Attached: <a href={getPreviewUrl(attachedDoc.UploadPath)} target="_blank" rel="noreferrer" style={{color: 'green', textDecoration: 'underline'}}>{attachedDoc.FileName}</a>
                </span>
            ) : 
            error ? error :
            repoDoc ? 'Available in your vault - Click "Attach Existing"' : 'Required'
        }
        secondaryTypographyProps={{ 
            color: error ? 'error' : attachedDoc ? 'success.main' : 'text.secondary',
            fontWeight: attachedDoc ? 'medium' : 'normal'
        }}
      />

      <Box sx={{ minWidth: 200, textAlign: 'right', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
        {attachedDoc ? (
          <Button variant="contained" color="success" size="small" disabled startIcon={<CheckCircleIcon />}>
             Attached
          </Button>
        ) : status === 'uploading' || status === 'attaching' ? (
          <Box sx={{ width: '100px' }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="caption" color="text.secondary">{status === 'uploading' ? 'Uploading...' : 'Attaching...'}</Typography>
          </Box>
        ) : (
          <>
            {repoDoc && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<AttachFileIcon />}
                onClick={handleAttach}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Attach Existing
              </Button>
            )}
            <Button variant="contained" component="label" size="small">
              Upload New
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" hidden onChange={handleUpload} />
            </Button>
          </>
        )}
      </Box>
    </ListItem>
  );
};

/* --------------------------------------------------------------
   Main Step Component
   -------------------------------------------------------------- */
const DocumentUploadStep = () => {
  const context = useOutletContext();
  const { applicationId, setErrors } = context || {}; 
  const { token } = useAuth();

  const [personalDocuments, setPersonalDocuments] = useState([]);
  const [applicationDocuments, setApplicationDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const documentTypes = [
    { type: 'ID_DOCUMENT', label: "Certified copy of Applicant's ID Document" },
    { type: 'ACADEMIC_TRANSCRIPT', label: 'Latest Academic Results/Transcript' },
    { type: 'PROOF_OF_REGISTRATION', label: 'Proof of Acceptance or Registration' },
    { type: 'PAYSLIP_MEMBER', label: "Certified and Latest Payslip of Guardian/Member" },
  ];

  const fetchData = useCallback(async () => {
    if (!token || !applicationId || applicationId === 'documents') return;

    try {
      const [allUserDocsRes, appDocsRes] = await Promise.all([
        api.get('/applicant/documents', { headers: { Authorization: `Bearer ${token}` } }),
        api.get(`/applicant/application/${applicationId}/documents`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setPersonalDocuments(allUserDocsRes.data || []);
      setApplicationDocuments(appDocsRes.data || []);
      
    } catch (err) {
      console.error("Error fetching docs:", err);
      setError('Failed to load documents.');
    } finally {
      setLoading(false);
    }
  }, [token, applicationId]);

  useEffect(() => {
    if (applicationId) fetchData();
    else setLoading(false);
  }, [applicationId, fetchData]);

  useEffect(() => {
      if (!setErrors) return;

      const missingDocs = documentTypes.filter(req => 
          !applicationDocuments.some(attached => 
              (attached.DocumentType === req.type) || (attached.FileType === req.type)
          )
      );

      if (missingDocs.length > 0) {
          const missingList = missingDocs.map(d => d.label.split(' ')[0] + '...').join(', ');
          setErrors(prev => ({ 
              ...prev, 
              documents: `Missing: ${missingList}` 
          }));
      } else {
          setErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors.documents;
              return newErrors;
          });
      }
  }, [applicationDocuments, setErrors]);

  return (
    <>
      <Typography variant="h6" gutterBottom>Upload &amp; Attach Documents</Typography>

      {!applicationId && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Save your application as a draft first to enable document management.
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        Please ensure all required documents are uploaded AND attached. A green "Attached" button confirms completion.
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <Paper variant="outlined" sx={{ mb: 3 }}>
          <List>
            {documentTypes.map(doc => (
              <DocumentManagerItem
                key={doc.type}
                documentType={doc.type}
                label={doc.label}
                applicationId={applicationId}
                token={token}
                personalDocuments={personalDocuments}
                applicationDocuments={applicationDocuments}
                refreshData={fetchData}
              />
            ))}
          </List>
        </Paper>
      )}
    </>
  );
};

export default DocumentUploadStep;
